import { useState, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import PdfUploader from "@/components/PdfUploader";
import { nhsAPI, exportAPI, userAPI, sessionAPI } from "@/lib/api";
import { FileText, Loader2, FileDown, CheckCircle, ArrowRight, ArrowLeft, BookOpen, Upload, X, Clock, ClipboardList, BarChart3, AlertTriangle, XCircle, RefreshCw, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const PROFESSIONS = [
  "Doctor", "Nurse", "Physiotherapist", "Occupational Therapist",
  "Speech & Language Therapist", "Radiographer", "Pharmacist",
  "Paramedic", "Biomedical Scientist", "Dietitian", "Podiatrist", "Psychologist", "Other"
];

const BANDS = ["Band 4", "Band 5", "Band 6", "Band 7", "Band 8a", "Band 8b", "Band 8c", "Band 8d"];

function generateRequestId() {
  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export default function NHSGenerator() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [pollMessage, setPollMessage] = useState("");
  const [result, setResult] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const [cvText, setCvText] = useState("");
  const [usageInfo, setUsageInfo] = useState(null);
  const [editedStatement, setEditedStatement] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);

  const [form, setForm] = useState({
    profession: "", band: "", job_description: "", person_specification: "",
    years_experience: "", current_role: "", clinical_specialties: "",
    leadership_experience: "", cpd_training: "", audit_research: "",
    governance_exposure: "", trust_values: "", achievements: "",
  });

  // Fetch usage on mount
  useEffect(() => {
    userAPI.getUsage().then(res => setUsageInfo(res.data)).catch(() => {});
  }, []);

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleCvExtracted = (text, filename) => {
    setCvText(text);
    setCvFile(filename);
    toast.success("CV uploaded successfully");
  };

  const clearCv = () => {
    setCvText("");
    setCvFile(null);
  };

  const pollForResult = async (requestId, maxAttempts = 30) => {
    setPolling(true);
    setPollMessage("Your supporting information is being generated. This may take up to 2 minutes...");
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      await new Promise(r => setTimeout(r, 5000));
      try {
        const res = await nhsAPI.status(requestId);
        const data = res.data;
        if (data.status === "success") {
          setResult(data);
          setStep(4);
          toast.success("Supporting information generated!");
          userAPI.getUsage().then(r => setUsageInfo(r.data)).catch(() => {});
          setPolling(false);
          setLoading(false);
          setPollMessage("");
          return;
        } else if (data.status === "failed") {
          toast.error(data.error || "Generation failed");
          setPolling(false);
          setLoading(false);
          setPollMessage("");
          return;
        }
        // Still pending
        const elapsed = attempt * 5;
        if (elapsed > 60) {
          setPollMessage("Almost there — finalising your supporting information...");
        } else if (elapsed > 30) {
          setPollMessage("AI is crafting your supporting information. Please hold on...");
        }
      } catch {
        // Poll request itself failed, keep trying
      }
    }
    // Max attempts reached
    toast.error("Generation is taking longer than expected. Please check Recent Activity on your Dashboard shortly.");
    setPolling(false);
    setLoading(false);
    setPollMessage("");
  };

  const handleGenerate = async () => {
    if (!form.profession || !form.band || !form.job_description || !form.person_specification) {
      toast.error("Please fill in the required fields");
      return;
    }
    setLoading(true);
    setResult(null);
    setPolling(false);
    setPollMessage("");

    const clientRequestId = generateRequestId();

    try {
      const payload = { ...form, client_request_id: clientRequestId };
      if (cvText) {
        payload.cv_text = cvText;
      }
      const res = await nhsAPI.generate(payload);
      setResult(res.data);
      setStep(4);
      toast.success("Supporting information generated!");
      userAPI.getUsage().then(r => setUsageInfo(r.data)).catch(() => {});
      setLoading(false);
    } catch (err) {
      const status = err.response?.status;
      const isTimeout = !err.response || status === 502 || status === 504 || err.code === "ECONNABORTED";

      if (isTimeout) {
        // Backend is still processing — switch to polling
        pollForResult(clientRequestId);
      } else {
        toast.error(err.response?.data?.detail || "Generation failed");
        setLoading(false);
      }
    }
  };

  const handleNew = async () => {
    try {
      await sessionAPI.reset({});
    } catch {
      // ignore reset errors
    }
    setStep(1);
    setResult(null);
    setCvText("");
    setCvFile(null);
    setForm({
      profession: "", band: "", job_description: "", person_specification: "",
      years_experience: "", current_role: "", clinical_specialties: "",
      leadership_experience: "", cpd_training: "", audit_research: "",
      governance_exposure: "", trust_values: "", achievements: "",
    });
  };


  const handleReanalyze = async () => {
    const textToAnalyze = isEditing ? editedStatement : result?.supporting_statement;
    if (!textToAnalyze?.trim() || !form.person_specification?.trim()) {
      toast.error("Supporting Information and Person Specification are required for re-analysis");
      return;
    }
    setReanalyzing(true);
    try {
      const res = await nhsAPI.reanalyze({
        si_text: textToAnalyze,
        ps_text: form.person_specification,
        jd_text: form.job_description || "",
      });
      setResult(prev => ({
        ...prev,
        supporting_statement: textToAnalyze,
        word_count: textToAnalyze.trim().split(/\s+/).length,
        ps_coverage: res.data.ps_coverage,
      }));
      if (isEditing) setIsEditing(false);
      toast.success("PS coverage updated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Re-analysis failed");
    } finally {
      setReanalyzing(false);
    }
  };

  const handleExport = async () => {
    if (!result) return;
    try {
      const mappingTable = (result.criteria_mapping || []).map(
        m => `- **${m.criterion}** (${m.type}${m.coverage_strength ? `, ${m.coverage_strength}` : ''}): ${m.evidence_summary || m.evidence || ''}`
      ).join("\n");
      const content = [
        `# NHS Supporting Information`,
        `## Supporting Statement\n${result.supporting_statement}`,
        `## Criteria Mapping\n${mappingTable}`,
        result.interview_questions?.length ? `## Interview Preparation\n${result.interview_questions.map((q, i) => `${i + 1}. ${q}`).join("\n")}` : "",
        result.tips?.length ? `## Tips\n${result.tips.map(t => `- ${t}`).join("\n")}` : "",
      ].filter(Boolean).join("\n\n");
      const res = await exportAPI.docx({ content, title: "NHS Supporting Information" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "NHS_Supporting_Information.docx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Document downloaded!");
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="nhs-generator-page">
        <div>
          <h1 className="text-2xl font-bold text-[#003087]" style={{ fontFamily: "Public Sans" }}>NHS SI Generator</h1>
          <p className="text-slate-500 mt-1">Generate structured supporting statements mapped to job criteria.</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2" data-testid="step-indicator">
          {[1, 2, 3, 4].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${step >= s ? "bg-[#003087] text-white" : "bg-slate-200 text-slate-500"}`}>
                {step > s ? <CheckCircle className="w-4 h-4" /> : s}
              </div>
              {s < 4 && <div className={`w-8 h-0.5 ${step > s ? "bg-[#003087]" : "bg-slate-200"}`} />}
            </div>
          ))}
          <span className="text-sm text-slate-500 ml-2">
            {step === 1 ? "Role Details" : step === 2 ? "Job Information" : step === 3 ? "Experience" : "Results"}
          </span>
        </div>

        {/* Usage Info Banner */}
        {usageInfo && usageInfo.plan_config && (
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="usage-banner">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-[#003087]/10 text-[#003087]">{usageInfo.plan_config.name} Plan</Badge>
              <span className="text-sm text-slate-600">
                {usageInfo.plan_config.nhs_gen_limit === -1 
                  ? "Unlimited generations" 
                  : `${usageInfo.usage?.nhs_gen || 0} of ${usageInfo.plan_config.nhs_gen_limit} used this month`}
              </span>
            </div>
            {usageInfo.plan_config.nhs_gen_limit !== -1 && (
              <span className="text-sm font-medium text-[#00A3A6]">
                {Math.max(0, usageInfo.plan_config.nhs_gen_limit - (usageInfo.usage?.nhs_gen || 0))} remaining
              </span>
            )}
          </div>
        )}

        {/* Step 1: Role Details */}
        {step === 1 && (
          <Card className="bg-white border border-slate-200 shadow-sm rounded-xl animate-fade-in" data-testid="nhs-step-1">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: "Public Sans" }}>Role Details</CardTitle>
              <CardDescription>Tell us about the position you're applying for.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Optional CV Upload */}
              <div className="p-4 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                <div className="flex items-start gap-3">
                  <Upload className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div className="flex-1">
                    <Label className="text-slate-700 font-medium">Upload your CV (Optional)</Label>
                    <p className="text-sm text-slate-500 mt-1">Uploading your CV helps generate more personalised and contextually accurate supporting information.</p>
                    {cvFile ? (
                      <div className="flex items-center gap-2 mt-3 p-2 bg-white rounded border border-slate-200">
                        <FileText className="w-4 h-4 text-[#00A3A6]" />
                        <span className="text-sm text-slate-700 flex-1 truncate">{cvFile}</span>
                        <Button variant="ghost" size="sm" onClick={clearCv} className="h-6 w-6 p-0" data-testid="clear-cv-btn">
                          <X className="w-4 h-4 text-slate-400" />
                        </Button>
                      </div>
                    ) : (
                      <div className="mt-3">
                        <PdfUploader onTextExtracted={handleCvExtracted} label="Upload CV (PDF or DOCX)" data-testid="cv-upload-btn" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-700">Profession *</Label>
                  <Select value={form.profession} onValueChange={(v) => updateForm("profession", v)}>
                    <SelectTrigger data-testid="nhs-profession-select">
                      <SelectValue placeholder="Select profession" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROFESSIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700">Band *</Label>
                  <Select value={form.band} onValueChange={(v) => updateForm("band", v)}>
                    <SelectTrigger data-testid="nhs-band-select">
                      <SelectValue placeholder="Select band" />
                    </SelectTrigger>
                    <SelectContent>
                      {BANDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end">
                <Button className="bg-[#003087] text-white hover:bg-[#002366]" onClick={() => setStep(2)} disabled={!form.profession || !form.band} data-testid="nhs-next-1-btn">
                  Next <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Job Info */}
        {step === 2 && (
          <Card className="bg-white border border-slate-200 shadow-sm rounded-xl animate-fade-in" data-testid="nhs-step-2">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: "Public Sans" }}>Job Information</CardTitle>
              <CardDescription>Provide the job description and person specification.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-700">Job Description *</Label>
                  <PdfUploader onTextExtracted={(text) => updateForm("job_description", text)} label="Upload JD (PDF or DOCX)" />
                </div>
                <Textarea
                  placeholder="Paste the job description here..."
                  className="min-h-[160px]"
                  value={form.job_description}
                  onChange={(e) => updateForm("job_description", e.target.value)}
                  data-testid="nhs-jd-input"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-700">Person Specification *</Label>
                  <PdfUploader onTextExtracted={(text) => updateForm("person_specification", text)} label="Upload PS (PDF or DOCX)" />
                </div>
                <Textarea
                  placeholder="Paste the person specification (essential/desirable criteria)..."
                  className="min-h-[160px]"
                  value={form.person_specification}
                  onChange={(e) => updateForm("person_specification", e.target.value)}
                  data-testid="nhs-ps-input"
                />
              </div>
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)} data-testid="nhs-back-2-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                <Button className="bg-[#003087] text-white hover:bg-[#002366]" onClick={() => setStep(3)} disabled={!form.job_description || !form.person_specification} data-testid="nhs-next-2-btn">
                  Next <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Experience */}
        {step === 3 && (
          <Card className="bg-white border border-slate-200 shadow-sm rounded-xl animate-fade-in" data-testid="nhs-step-3">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: "Public Sans" }}>Your Experience <span className="text-sm font-normal text-slate-400">(optional — helps personalise the output)</span></CardTitle>
              <CardDescription>Optional details to personalise your supporting information.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-700">Years of Experience</Label>
                  <Input placeholder="e.g. 5 years" value={form.years_experience} onChange={(e) => updateForm("years_experience", e.target.value)} data-testid="nhs-years-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700">Current/Previous Role</Label>
                  <Input placeholder="e.g. Band 6 Nurse" value={form.current_role} onChange={(e) => updateForm("current_role", e.target.value)} data-testid="nhs-role-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Clinical Specialties</Label>
                <Input placeholder="e.g. Emergency Care, Paediatrics" value={form.clinical_specialties} onChange={(e) => updateForm("clinical_specialties", e.target.value)} data-testid="nhs-specialties-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Leadership Experience</Label>
                <Textarea placeholder="Describe any leadership, mentoring, or management experience..." className="min-h-[80px]" value={form.leadership_experience} onChange={(e) => updateForm("leadership_experience", e.target.value)} data-testid="nhs-leadership-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">CPD & Training</Label>
                <Textarea placeholder="List relevant training, courses, or certifications..." className="min-h-[80px]" value={form.cpd_training} onChange={(e) => updateForm("cpd_training", e.target.value)} data-testid="nhs-cpd-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-700">Audit/Research</Label>
                  <Textarea placeholder="Any audit or research involvement..." className="min-h-[80px]" value={form.audit_research} onChange={(e) => updateForm("audit_research", e.target.value)} data-testid="nhs-audit-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700">Governance Exposure</Label>
                  <Textarea placeholder="Governance, quality improvement..." className="min-h-[80px]" value={form.governance_exposure} onChange={(e) => updateForm("governance_exposure", e.target.value)} data-testid="nhs-governance-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Trust Values Alignment</Label>
                <Input placeholder="e.g. Compassion, Excellence, Collaboration" value={form.trust_values} onChange={(e) => updateForm("trust_values", e.target.value)} data-testid="nhs-values-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Measurable Achievements</Label>
                <Textarea placeholder="Any quantifiable achievements or outcomes..." className="min-h-[80px]" value={form.achievements} onChange={(e) => updateForm("achievements", e.target.value)} data-testid="nhs-achievements-input" />
              </div>
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)} data-testid="nhs-back-3-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                <Button className="bg-[#003087] text-white hover:bg-[#002366]" onClick={handleGenerate} disabled={loading} data-testid="nhs-generate-btn">
                  {loading ? (
                    polling ? <><Clock className="w-4 h-4 mr-2 animate-pulse" /> Processing...</> : <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                  ) : <><FileText className="w-4 h-4 mr-2" /> Generate</>}
                </Button>
              </div>
              {polling && pollMessage && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2" data-testid="nhs-polling-banner">
                  <Clock className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0 animate-pulse" />
                  <p className="text-sm text-blue-700">{pollMessage}</p>
                </div>
              )}
              {loading && (
                <p className="text-xs text-slate-400 text-center mt-2" data-testid="nhs-processing-hint">
                  Generating your Supporting Information. This can take up to 2 minutes. Please wait...
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 4: Results */}
        {step === 4 && result && (
          <div className="space-y-4 animate-fade-in" data-testid="nhs-results">
            <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg" style={{ fontFamily: "Public Sans" }}>Generated Supporting Information</CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => {
                      const text = isEditing ? editedStatement : result.supporting_statement;
                      navigator.clipboard.writeText(text).then(() => {
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                        toast.success("Copied to clipboard");
                      });
                    }} data-testid="nhs-copy-btn">
                      {copied ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy</>}
                    </Button>
                    {!isEditing ? (
                      <Button variant="outline" size="sm" onClick={() => { setEditedStatement(result.supporting_statement); setIsEditing(true); }} data-testid="nhs-edit-btn">Edit</Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => setIsEditing(false)} data-testid="nhs-cancel-edit-btn">Cancel</Button>
                    )}
                    {(isEditing || result.ps_coverage) && (
                      <Button variant="outline" size="sm" className="border-[#003087] text-[#003087] hover:bg-[#003087]/5" onClick={handleReanalyze} disabled={reanalyzing} data-testid="nhs-reanalyze-btn">
                        {reanalyzing ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Analyzing...</> : <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Re-analyze</>}
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={handleNew} data-testid="nhs-new-btn">New</Button>
                    <Button size="sm" className="bg-[#003087] text-white hover:bg-[#002366]" onClick={handleExport} data-testid="nhs-export-btn">
                      <FileDown className="w-4 h-4 mr-1" /> Export .docx
                    </Button>
                  </div>
                </div>
                {result.word_count && <p className="text-sm text-slate-500">{result.word_count} / 1500 words{isEditing ? " (editing)" : ""}</p>}
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <Textarea
                    className="min-h-[300px] text-sm leading-relaxed font-normal"
                    value={editedStatement}
                    onChange={e => setEditedStatement(e.target.value)}
                    data-testid="nhs-edit-textarea"
                  />
                ) : (
                  <div className="prose max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap text-sm" data-testid="nhs-statement-text">
                    {result.supporting_statement}
                  </div>
                )}
              </CardContent>
            </Card>

            <p className="text-xs text-slate-400 text-center" data-testid="nhs-review-reminder">
              Review your supporting information before submitting your NHS application.
            </p>

            {/* Criteria Mapping */}
            {result.criteria_mapping?.length > 0 && (
              <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="nhs-criteria-mapping">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#00A3A6]" /> Criteria Mapping</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {result.criteria_mapping.map((m, i) => (
                      <div key={i} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            m.coverage_strength === "strong" ? "bg-[#007F3B]" :
                            m.coverage_strength === "partial" ? "bg-[#FFB81C]" :
                            "bg-[#D5281B]"
                          }`} />
                          <span className="text-sm font-medium text-slate-900">{m.criterion}</span>
                          <Badge className={m.type === "essential" ? "bg-[#003087]/10 text-[#003087]" : "bg-slate-100 text-slate-600"} variant="outline">{m.type}</Badge>
                          {m.coverage_strength && (
                            <Badge className={
                              m.coverage_strength === "strong" ? "bg-[#007F3B]/10 text-[#007F3B]" :
                              m.coverage_strength === "partial" ? "bg-[#FFB81C]/10 text-[#996B00]" :
                              "bg-[#D5281B]/10 text-[#D5281B]"
                            } variant="outline">{m.coverage_strength}</Badge>
                          )}
                        </div>
                        {(m.evidence_summary || m.evidence) && <p className="text-sm text-slate-600 ml-4">{m.evidence_summary || m.evidence}</p>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* PS Coverage + Shortlisting Simulation */}
            {result.ps_coverage && (
              <>
                <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="nhs-ps-coverage-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2"><ClipboardList className="w-4 h-4 text-[#003087]" /> Person Specification Coverage</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-slate-50 rounded-lg text-center">
                        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Essential Criteria</p>
                        <p className="text-2xl font-bold" style={{ color: result.ps_coverage.essential_coverage_pct >= 70 ? "#007F3B" : result.ps_coverage.essential_coverage_pct >= 40 ? "#FFB81C" : "#D5281B" }}>{result.ps_coverage.essential_coverage_pct}%</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg text-center">
                        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Desirable Criteria</p>
                        <p className="text-2xl font-bold" style={{ color: result.ps_coverage.desirable_coverage_pct >= 70 ? "#007F3B" : result.ps_coverage.desirable_coverage_pct >= 40 ? "#FFB81C" : "#D5281B" }}>{result.ps_coverage.desirable_coverage_pct}%</p>
                      </div>
                    </div>
                    {result.ps_coverage.criteria_results?.length > 0 && (
                      <div className="space-y-1.5 pt-1">
                        {result.ps_coverage.criteria_results.map((c, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${c.score >= 3 ? "bg-[#007F3B]" : c.score >= 2 ? "bg-[#FFB81C]" : c.score >= 1 ? "bg-orange-400" : "bg-[#D5281B]"}`} />
                            <span className="flex-1 text-slate-700 truncate">{c.criterion}</span>
                            <Badge variant="outline" className={`text-[10px] ${c.type === "essential" ? "border-blue-200 text-blue-700" : "border-slate-200 text-slate-500"}`}>{c.type}</Badge>
                            <span className="text-slate-400 w-8 text-right">{c.score}/3</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-white border border-slate-200 shadow-sm rounded-xl" data-testid="nhs-shortlisting-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#00A3A6]" /> Shortlisting Simulation</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-slate-500 font-medium">Estimated Panel Score</p>
                        <p className="text-2xl font-bold text-slate-900">{result.ps_coverage.panel_score} / {result.ps_coverage.panel_max}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500 font-medium">Likely Outcome</p>
                        <p className={`text-sm font-semibold ${result.ps_coverage.essential_coverage_pct >= 80 ? "text-[#007F3B]" : result.ps_coverage.essential_coverage_pct >= 50 ? "text-[#9A6700]" : "text-[#D5281B]"}`}>
                          {result.ps_coverage.likely_outcome}
                        </p>
                      </div>
                    </div>
                    {(result.ps_coverage.missing_evidence?.length > 0 || result.ps_coverage.weak_evidence?.length > 0) && (
                      <div className="pt-2 border-t border-slate-100 space-y-1.5">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Gaps Identified</p>
                        {result.ps_coverage.missing_evidence?.map((m, i) => (
                          <div key={`m-${i}`} className="flex items-start gap-2 text-sm">
                            <XCircle className="w-3.5 h-3.5 text-[#D5281B] mt-0.5 flex-shrink-0" />
                            <span className="text-slate-700">{m}</span>
                          </div>
                        ))}
                        {result.ps_coverage.weak_evidence?.map((w, i) => (
                          <div key={`w-${i}`} className="flex items-start gap-2 text-sm">
                            <AlertTriangle className="w-3.5 h-3.5 text-[#FFB81C] mt-0.5 flex-shrink-0" />
                            <span className="text-slate-700">{w}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            {/* Interview Questions */}
            {result.interview_questions?.length > 0 && (
              <Card className="bg-white border border-slate-200 shadow-sm rounded-xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Interview Preparation Questions</CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-2 list-decimal list-inside">
                    {result.interview_questions.map((q, i) => (
                      <li key={i} className="text-sm text-slate-700">{q}</li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
